/**
 * src/gm/employees/parser.ts — 模型输出解析与校验（三条流水线 + 各阶段校验器，全继承）。
 *
 * 浏览器端校验栈：正则/DOMParser 提取 <script> 做结构检查，语法校验下沉到
 * 冒烟沙箱（探针 window.onerror 捕获，带行号）——不在主页面用 new Function/eval，
 * CSP 无需 'unsafe-eval'（事故尸检 R3 修复，设计稿 §9.5）。
 * 所有解析失败统一抛 ParseError，由重试协议消费。
 */

import { ParseError } from '../platform/errors'
import type { Decision, Project } from '../platform/save-types'

// ── 流水线 1：结构化输出 ────────────────────────────────────────────────────

/** 优先直接 JSON.parse（response_format 成功时），降级提取首个 ```json 围栏块 */
export function parseStructured<T>(raw: string): T {
  const trimmed = raw.trim()
  try {
    return JSON.parse(trimmed) as T
  } catch {
    // 降级：```json 围栏
  }
  const fence = /```(?:json)?\s*\n([\s\S]*?)```/.exec(trimmed)
  if (fence) {
    try {
      return JSON.parse(fence[1].trim()) as T
    } catch {
      // 继续抛
    }
  }
  throw new ParseError('输出不是合法 JSON（直接解析与 ```json 围栏提取均失败）')
}

// ── 流水线 2：分隔符协议（美术 ===ART_SPEC=== / ===ASSETS_CSS=== / ===END===）──

export function parseDelimited(raw: string, markers: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (let i = 0; i < markers.length; i++) {
    const start = raw.indexOf(markers[i])
    if (start < 0) {
      throw new ParseError(`缺少必需段 ${markers[i]}`, [`缺少必需段 ${markers[i]}`])
    }
    const from = start + markers[i].length
    const next = i + 1 < markers.length ? raw.indexOf(markers[i + 1], from) : -1
    out[markers[i]] = (next >= 0 ? raw.slice(from, next) : raw.slice(from)).trim()
  }
  return out
}

// ── 流水线 3：代码块提取（程序 index.html）──────────────────────────────────

/** 优先 ```html 围栏全文；无围栏且以 <!DOCTYPE/<html 开头则全文采用；其余判失败 */
export function parseCodeBlock(raw: string, lang: string): string {
  const fence = new RegExp('```' + lang + '\\s*\\n([\\s\\S]*?)```', 'i').exec(raw)
  if (fence) return fence[1].trim()
  const anyFence = /```\s*\n([\s\S]*?)```/.exec(raw)
  if (anyFence && /<!DOCTYPE|<html/i.test(anyFence[1])) return anyFence[1].trim()
  const trimmed = raw.trim()
  if (/^(<!DOCTYPE|<html)/i.test(trimmed)) return trimmed
  throw new ParseError('没提取到 HTML：既无 ```html 围栏，输出也不以 <!DOCTYPE/<html 开头')
}

// ── 策划决策校验（剧本 §3.3 逐条）────────────────────────────────────────────

export interface PlanPayload {
  decisions: Decision[]
  design_doc: string
}

export function validateDecisions(parsed: unknown, project: Project, designerId: string): PlanPayload {
  const issues: string[] = []
  const p = parsed as Partial<PlanPayload> | null
  const list = p?.decisions
  if (!Array.isArray(list)) {
    throw new ParseError('decisions 字段缺失或不是数组')
  }
  if (list.length < 1 || list.length > 2) {
    issues.push(`决策数组长度必须为 1-2，实际 ${list.length}（0 个会让玩家永远拍不了板）`)
  }
  const seenIds = new Set<string>()
  for (const [i, d] of list.entries()) {
    const tag = `第 ${i + 1} 个决策`
    if (typeof d !== 'object' || d === null) {
      issues.push(`${tag}不是对象`)
      continue
    }
    if (!/^dec_[a-z0-9_]+$/.test(d.id ?? '')) issues.push(`${tag} id「${d.id}」不匹配 ^dec_[a-z0-9_]+$`)
    if (seenIds.has(d.id)) issues.push(`${tag} id「${d.id}」与前面的决策重复`)
    seenIds.add(d.id)
    if (d.project_id !== project.id) issues.push(`${tag} project_id 应为 ${project.id}，实际「${d.project_id}」`)
    if (d.raised_by !== designerId) issues.push(`${tag} raised_by 应为 ${designerId}，实际「${d.raised_by}」`)
    if (!['gameplay_variant', 'visual_direction'].includes(d.kind)) {
      issues.push(`${tag} kind 必须是 gameplay_variant / visual_direction，实际「${d.kind}」`)
    }
    if (!d.title?.trim()) issues.push(`${tag} 缺 title`)
    if (!d.description?.trim()) issues.push(`${tag} 缺 description`)
    if (!Array.isArray(d.options) || d.options.length !== 3) {
      issues.push(`${tag} options 必须恰好 3 项，实际 ${Array.isArray(d.options) ? d.options.length : '非数组'}`)
    } else {
      const styles = d.options.map((o) => o?.style).sort()
      if (styles.join(',') !== 'bold,safe,signature') {
        issues.push(`${tag} 三选项 style 必须恰为 safe/bold/signature 各一，实际 [${styles.join(', ')}]`)
      }
      const keys = d.options.map((o) => o?.key).sort()
      if (keys.join(',') !== 'a,b,c') {
        issues.push(`${tag} 三选项 key 必须恰为 a/b/c 各一，实际 [${keys.join(', ')}]`)
      }
      for (const o of d.options) {
        if (!o?.label?.trim() || !o?.description?.trim() || !o?.expected_impact?.trim()) {
          issues.push(`${tag} 存在缺 label/description/expected_impact 的选项`)
          break
        }
      }
    }
    if (d.status !== 'pending') issues.push(`${tag} status 必须为 "pending"，实际「${d.status}」`)
    if (d.player_choice !== null) issues.push(`${tag} player_choice 必须为 null`)
  }
  if (issues.length > 0) throw new ParseError(`决策校验未过（${issues.length} 项）`, issues)
  return { decisions: list as Decision[], design_doc: typeof p?.design_doc === 'string' ? p.design_doc : '' }
}

// ── 美术校验（两段齐全 + hex ≤16 + 动效关键词防糊弄）────────────────────────

export interface ArtPayload {
  artSpec: string
  assetsCss: string
}

export function validateArt(artSpec: string, assetsCss: string): ArtPayload {
  const issues: string[] = []
  if (artSpec.length < 200) issues.push(`art-spec 太短（${artSpec.length} 字符），规格书不可能这么薄`)
  const hexes = new Set((artSpec.match(/#[0-9a-fA-F]{6}\b/g) ?? []).map((h) => h.toLowerCase()))
  if (hexes.size === 0) issues.push('art-spec 中没有任何 hex 色值（色板清单缺失）')
  if (hexes.size > 16) issues.push(`色板超编：去重后 ${hexes.size} 色 > 16 色纪律`)
  if (!/(动效|动画|过渡|闪烁|抖动|粒子|animation|transition)/i.test(artSpec)) {
    issues.push('art-spec 缺少反馈动效规格（未检出"动效/动画/transition"等描述）')
  }
  if (!/(触发|按下|命中|得分|失败|通关|悬停)/.test(artSpec)) {
    issues.push('art-spec 的动效缺少触发事件描述（命中/得分/失败/通关 等）')
  }
  if (issues.length > 0) throw new ParseError(`美术规格校验未过（${issues.length} 项）`, issues)
  return { artSpec, assetsCss }
}

// ── 程序校验（校验反馈 loop ①②：语法 + 契约；冒烟在验收环节）────────────────

/** 提取全部内联 <script> 内容（DOMParser 可用时优先，正则兜底——Node 测试环境无 DOM） */
export function extractScripts(html: string): string[] {
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      return Array.from(doc.querySelectorAll('script'))
        .filter((s) => !s.src)
        .map((s) => s.textContent ?? '')
        .filter((t) => t.trim().length > 0)
    } catch {
      // 落到正则
    }
  }
  const out: string[] = []
  const re = /<script\b[^>]*>([\s\S]*?)<\/script>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    const tag = m[0].slice(0, m[0].indexOf('>'))
    if (/\bsrc\s*=/i.test(tag)) continue // 外链脚本本来就被契约禁，跳过
    if (m[1].trim()) out.push(m[1])
  }
  return out
}

export function validateIndexHtml(html: string): string {
  const issues: string[] = []
  if (html.length < 500) issues.push(`成品太短（${html.length} 字符），不像一个完整游戏`)
  if (!/<\/html>\s*$/.test(html)) issues.push('输出疑似被截断：不以 </html> 结尾')

  // ① 语法检查【事故尸检 R3 修复】：不在主页面用 new Function/eval——
  //    CSP 承诺不加 'unsafe-eval'。语法错误由冒烟探针在沙箱内捕获：
  //    window.onerror 带行号上报 SyntaxError，此处只做结构检查，职责分离。
  const scripts = extractScripts(html)
  if (scripts.length === 0) issues.push('没有找到任何内联 <script>——游戏逻辑缺失')

  // ② 契约检查：无外链资源 / 无 TODO / 有核心循环痕迹
  if (/(?:src|href)\s*=\s*["']https?:\/\//i.test(html)) {
    issues.push('存在 http(s) 外链资源（src/href）——契约要求零依赖单文件')
  }
  if (/@import|url\(\s*["']?https?:\/\//i.test(html)) {
    issues.push('CSS 中存在 @import / url(http…) 外链——字体 CDN 也禁')
  }
  if (/\b(TODO|FIXME|PLACEHOLDER)\b/i.test(html)) issues.push('存在 TODO/FIXME/占位符残留')
  // ③ 沙箱禁令（M5 尸检 B1 修复）：sandbox="allow-scripts" 下持久化 API 抛 SecurityError。
  //    硬失败——try/catch 守卫写法也不放行（与 prompt 禁令口径一致，不做辨别守卫的正则脆弱尝试）
  const storageHit = /\blocalStorage\b|\bsessionStorage\b|\bindexedDB\b|document\.cookie/.exec(html)
  if (storageHit) {
    issues.push(
      `检出 ${storageHit[0]}：沙箱禁用一切持久化 API（localStorage/sessionStorage/IndexedDB/cookie），运行时会抛 SecurityError 直接点不亮——需要记忆的状态改用内存变量`,
    )
  }
  const hasInput = /addEventListener|onclick|onkeydown|onkeyup|pointerdown|touchstart/i.test(html)
  const hasLoop = /requestAnimationFrame|setInterval|setTimeout/i.test(html)
  if (!hasInput || !hasLoop) {
    issues.push('缺少核心循环痕迹（需要输入监听 + rAF/定时器驱动）')
  }

  if (issues.length > 0) throw new ParseError(`成品校验未过（${issues.length} 项）`, issues)
  return html
}

// ── 评审分项校验（子 schema + 试玩协议无效判定）──────────────────────────────

export interface ReviewSubscore {
  scores: {
    completion: { score: number; comment: string }
    creativity: { score: number; comment: string }
    art: { score: number; comment: string }
    game_feel: { score: number; comment: string }
    theme_fit: { score: number; comment: string }
  }
  market_fit_notes: string
  overall_comment: string
  playthrough_log: string
  /** 评审员笔名：季度首评由模型自选，此后固定 */
  pen_name: string
}

const DIMENSIONS = ['completion', 'creativity', 'art', 'game_feel', 'theme_fit'] as const

export function validateReviewSub(parsed: unknown, smokeVerdict: 'PASS' | 'FAIL' | null): ReviewSubscore {
  const issues: string[] = []
  const p = parsed as Partial<ReviewSubscore> | null
  if (!p || typeof p !== 'object') throw new ParseError('评审输出不是对象')
  const scores = p.scores as ReviewSubscore['scores'] | undefined
  if (!scores || typeof scores !== 'object') {
    issues.push('scores 缺失')
  } else {
    for (const dim of DIMENSIONS) {
      const d = scores[dim]
      if (!d || typeof d !== 'object') {
        issues.push(`scores.${dim} 缺失`)
        continue
      }
      if (!Number.isInteger(d.score) || d.score < 0 || d.score > 10) {
        issues.push(`scores.${dim}.score 必须是 0-10 整数，实际 ${d.score}`)
      }
      if (!d.comment?.trim()) issues.push(`scores.${dim}.comment 缺失`)
    }
    // 冒烟锚定：FAIL → completion 必须 0-1（事故级）
    if (smokeVerdict === 'FAIL' && scores.completion && scores.completion.score > 1) {
      issues.push(`冒烟 verdict=FAIL 时 completion 必须锚定 0-1（事故级），实际 ${scores.completion.score}`)
    }
  }
  if (!p.market_fit_notes?.trim()) issues.push('market_fit_notes 缺失')
  if (!p.overall_comment?.trim()) issues.push('overall_comment 缺失')
  // 试玩协议：缺 playthrough_log 或未引用冒烟结论 → 评审无效
  if (!p.playthrough_log || p.playthrough_log.trim().length < 50) {
    issues.push('playthrough_log 缺失或过短（违反试玩协议：必须记录至少一局完整推演要点）')
  } else if (!/冒烟|smoke|PASS|FAIL/i.test(p.playthrough_log)) {
    issues.push('playthrough_log 未引用冒烟报告结论（违反试玩协议）')
  }
  if (!p.pen_name?.trim()) issues.push('pen_name 缺失（评审员必须署名）')
  if (issues.length > 0) throw new ParseError(`评审校验未过（${issues.length} 项）`, issues)
  return p as ReviewSubscore
}

/** 重试反馈消息：把校验失败清单变成给模型的修正要求 */
export function retryFeedback(issues: string[]): string {
  return (
    `上次交付未通过验收，请逐项修正后重新完整输出（只输出修正后的完整交付物，不要解释）：\n` +
    issues.map((s, i) => `${i + 1}. ${s}`).join('\n')
  )
}
